import React from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../firebaseConfig";
import { Button } from "antd";
import { HeartOutlined, HeartFilled, ClockCircleOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { useUserData } from "../../hooks/useUsers";
import { onAddFriend } from "../../hooks/onAddFriend";
import { getDays } from "../../lib/utils";

export default function LikeProfile({ id, userId }) {
  const dailyLimit = parseInt(process.env.REACT_APP_DAILY_LIKES_LIMIT);
  const [user] = useAuthState(auth);
  const { userData } = useUserData(id);
  const { loading, userData: friendData } = useUserData(userId);

  const [isFriendRequested, setIsFriendRequested] = React.useState(
    userData?.friendRequests && userData.friendRequests.some(friend => friend === user?.uid)
  );

  const [dailyLikes, setDailyLikes] = React.useState(
    friendData?.dailyLikes && friendData?.dailyLikes === dailyLimit && getDays(friendData && friendData?.lastUpdated) === 0
  );

  const [isFriendReceived, setIsFriendReceived] = React.useState(
    friendData?.friendRequests && friendData.friendRequests.some(friend => friend === id)
  );

  const [isFriendAdded, setIsFriendAdded] = React.useState(
    userData?.friendRequests && userData?.friends?.includes(user?.uid)
  );

  const [isResetLikes, setIsResetLikes] = React.useState(
    friendData?.dailyLikes && friendData?.dailyLikes === dailyLimit && getDays(friendData && friendData?.lastUpdated) !== 0
  )

  async function handleFriendAdd() {
    await onAddFriend(id, user.uid, isFriendRequested, isResetLikes);
  }

  React.useEffect(() => {
    if (loading) return;

    if (friendData) {
      setIsFriendReceived(
        friendData?.friendRequests && friendData.friendRequests.some(friend => friend === id)
      );

      setDailyLikes(
        friendData?.dailyLikes && friendData?.dailyLikes === dailyLimit && getDays(friendData?.lastUpdated) === 0
      )

      setIsFriendRequested(
        userData?.friendRequests && userData?.friendRequests?.some(friend => friend === user?.uid)
      );
      setIsFriendAdded(
        userData?.friends && userData?.friends?.includes(user.uid)
      );

      setIsResetLikes(
        friendData?.dailyLikes === dailyLimit && getDays(friendData && friendData?.lastUpdated) !== 0
      )
    }
  }, [userData, loading, friendData, dailyLimit, user.uid, id]);

  const getButtonConfig = () => {
    if (dailyLikes) {
      return {
        icon: <ClockCircleOutlined />,
        text: "Daily Likes Used",
        style: {
          background: 'rgba(255, 193, 7, 0.1)',
          borderColor: '#ffc107',
          color: '#d48806'
        },
        disabled: true
      };
    }
    
    if (isFriendReceived) {
      return {
        icon: <HeartFilled />,
        text: "Interest Received",
        style: {
          background: 'rgba(82, 196, 26, 0.1)',
          borderColor: '#52c41a',
          color: '#389e0d'
        },
        disabled: true
      };
    }
    
    if (isFriendAdded) {
      return {
        icon: <CheckCircleOutlined />,
        text: "Interest Accepted",
        style: {
          background: 'rgba(82, 196, 26, 0.1)',
          borderColor: '#52c41a',
          color: '#389e0d'
        },
        disabled: true
      };
    }
    
    if (isFriendRequested) {
      return {
        icon: <HeartFilled />,
        text: "Interest Sent",
        style: {
          background: 'rgba(102, 126, 234, 0.1)',
          borderColor: '#667eea',
          color: '#667eea'
        },
        disabled: true
      };
    }
    
    return {
      icon: <HeartOutlined />,
      text: "Send Interest",
      style: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderColor: '#667eea',
        color: '#fff',
        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
      },
      disabled: false,
      onClick: handleFriendAdd
    };
  };

  const buttonConfig = getButtonConfig();

  return (
    <div style={{ 
      padding: '16px',
      textAlign: 'center'
    }}>
      {auth.currentUser && (
        <Button
          type={buttonConfig.disabled ? "default" : "primary"}
          disabled={buttonConfig.disabled}
          onClick={buttonConfig.onClick}
          size="large"
          style={{
            borderRadius: '12px',
            fontWeight: '600',
            fontSize: '14px',
            height: '48px',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.3s ease',
            ...buttonConfig.style
          }}
          icon={buttonConfig.icon}
          onMouseOver={(e) => {
            if (!buttonConfig.disabled) {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.5)';
            }
          }}
          onMouseOut={(e) => {
            if (!buttonConfig.disabled) {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
            }
          }}
        >
          {buttonConfig.text}
        </Button>
      )}
    </div>
  );
}